import { FeatureTypes } from "entity-app/constants";
import {
  LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  SUBMITTAL_LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  SUBMITTAL_SUCCESSOR_CARD_LEFT_PADDING_REDUCTION_PX,
  type FocusedSuccessorLayoutOptions
} from "./layout-constants";
import type { TEntityDateblock } from "./models";

/** How the predecessor / successor side columns are rendered for a focal entity. */
export type LinksColumnVariant = "generic" | "package-links" | "bid-planner";

export type EntityDateblockLayoutConfig = {
  entityFeatureTypeId: number | undefined;
  showPredecessors: boolean;
  predecessorColumn: LinksColumnVariant | null;
  successorColumn: LinksColumnVariant | null;
  featureGroupOrder: readonly FeatureTypes[];
  dateblock: {
    materialFocusedLayout: boolean;
    designPackageFocusedLayout: boolean;
  };
  successor: {
    /** Negative marginLeft shift for generic / design-package successors. */
    useGenericShift: boolean;
    /** Bid-package Date planner uses a dedicated margin formula. */
    useBidPackageMargin: boolean;
    axisWidthPx: number;
    cardLeftPaddingReductionPx: number;
    focusedCrossShift: FocusedSuccessorLayoutOptions;
  };
  /** Material entities show the empty submittal placeholder in predecessor groups. */
  showSubmittalGroupEmptyElement: boolean;
  isSubmittalFocalEntity: boolean;
};

const DEFAULT_FEATURE_GROUP_ORDER: readonly FeatureTypes[] = [
  FeatureTypes.SUBMITTALS,
  FeatureTypes.MATERIALS,
  FeatureTypes.ACTIVITIES
];

function getEntityFeatureTypeId(entity?: TEntityDateblock): number | undefined {
  return entity?.feature_type_id;
}

function getFeatureGroupOrderForEntity(
  featureTypeId: number | undefined
): readonly FeatureTypes[] {
  if (featureTypeId === FeatureTypes.BID_PACKAGE) {
    return [FeatureTypes.DESIGN_PACKAGE];
  }
  if (featureTypeId === FeatureTypes.DESIGN_PACKAGE) {
    return [FeatureTypes.BID_PACKAGE];
  }
  if (
    featureTypeId === FeatureTypes.SUBMITTALS ||
    featureTypeId === FeatureTypes.PLANNING_ONLY_SUBMITTAL
  ) {
    return [
      FeatureTypes.BID_PACKAGE,
      FeatureTypes.MATERIALS,
      FeatureTypes.ACTIVITIES
    ];
  }
  return DEFAULT_FEATURE_GROUP_ORDER;
}

function isSubmittalFocalEntity(featureTypeId: number | undefined): boolean {
  return (
    featureTypeId === FeatureTypes.SUBMITTALS ||
    featureTypeId === FeatureTypes.PLANNING_ONLY_SUBMITTAL
  );
}

/**
 * Single registry that maps a focal entity type to its three-column layout
 * behavior. Replaces scattered `isBidPackageEntity` / `hidePredecessors` checks
 * throughout the interface shell.
 */
export function getEntityDateblockLayoutConfig(
  entity?: TEntityDateblock
): EntityDateblockLayoutConfig {
  const entityFeatureTypeId = getEntityFeatureTypeId(entity);
  const isMaterial = entityFeatureTypeId === FeatureTypes.MATERIALS;
  const isDesignPackage = entityFeatureTypeId === FeatureTypes.DESIGN_PACKAGE;
  const isBidPackage = entityFeatureTypeId === FeatureTypes.BID_PACKAGE;
  const submittalFocal = isSubmittalFocalEntity(entityFeatureTypeId);

  const showPredecessors = entityFeatureTypeId !== FeatureTypes.DESIGN_PACKAGE;

  let predecessorColumn: LinksColumnVariant | null = "generic";
  if (!showPredecessors) {
    predecessorColumn = null;
  } else if (isBidPackage) {
    predecessorColumn = "package-links";
  }

  let successorColumn: LinksColumnVariant | null = "generic";
  if (isBidPackage) {
    successorColumn = "bid-planner";
  } else if (isDesignPackage) {
    successorColumn = "package-links";
  }

  const focusedCrossShift: FocusedSuccessorLayoutOptions = {
    materialFocusedLayout: isMaterial,
    designPackageFocusedLayout: isDesignPackage
  };

  return {
    entityFeatureTypeId,
    showPredecessors,
    predecessorColumn,
    successorColumn,
    featureGroupOrder: getFeatureGroupOrderForEntity(entityFeatureTypeId),
    dateblock: {
      materialFocusedLayout: isMaterial,
      designPackageFocusedLayout: isDesignPackage
    },
    successor: {
      useGenericShift: !isBidPackage,
      useBidPackageMargin: isBidPackage,
      axisWidthPx: submittalFocal
        ? SUBMITTAL_LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX
        : LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
      cardLeftPaddingReductionPx: submittalFocal
        ? SUBMITTAL_SUCCESSOR_CARD_LEFT_PADDING_REDUCTION_PX
        : 0,
      focusedCrossShift
    },
    showSubmittalGroupEmptyElement: isMaterial,
    isSubmittalFocalEntity: submittalFocal
  };
}
